import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { LucideIcon } from 'lucide-react';

interface FeatureShowcaseProps {
  icon: LucideIcon;
  title: string;
  description: string;
  features: string[];
  badge?: string;
  image?: ReactNode;
  imagePosition?: 'left' | 'right';
  className?: string;
}

export function FeatureShowcase({
  icon: Icon,
  title,
  description,
  features,
  badge,
  image,
  imagePosition = 'right',
  className = ''
}: FeatureShowcaseProps) {
  const content = (
    <div className="space-y-6">
      <div className="space-y-4">
        {badge && (
          <Badge variant="secondary" className="mb-2">
            {badge}
          </Badge>
        )}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold">{title}</h3>
        </div>
        <p className="text-lg text-muted-foreground">{description}</p>
      </div>

      <ul className="space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-primary" />
            </div>
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const imageContent = image && (
    <div className="relative">
      {image}
    </div>
  );

  return (
    <div className={`py-16 sm:py-24 ${className}`}>
      <div className="container max-w-6xl mx-auto px-4">
        <div className={`grid grid-cols-1 lg:grid-cols-[0.6fr_0.5fr] gap-12 items-center ${imagePosition === 'left' ? 'lg:grid-flow-dense' : ''
          }`}>
          <div className={imagePosition === 'left' ? 'lg:col-start-2' : ''}>
            {content}
          </div>
          {imageContent && (
            <div className={imagePosition === 'left' ? 'lg:col-start-1 lg:row-start-1' : ''}>
              {imageContent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
